import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface BigConcept {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  color: string;
  connectors: SmallConnector[];
  reinforcement: ReinforcementActivity;
}

interface SmallConnector {
  id: string;
  title: string;
  content: string;
  type: 'example' | 'story' | 'fact';
  completed: boolean;
}

interface ReinforcementActivity {
  id: string;
  type: '3-2-1' | 'scenario' | 'connection';
  prompt: string;
  options?: string[];
  correctAnswer?: string;
  explanation?: string;
}

const ADDITIONAL_EXAMPLES = [
  "Tanda (Mexico): Rotating savings groups with 95% completion rates, serving people banks won't touch",
  "Kiva: $1.7 billion loaned through community trust, 96.2% repayment rate across 80+ countries",
  "LendingClub early data: Personal connections reduced defaults by 40% vs anonymous lending",
  "Grameen America: 99.9% repayment rate in US cities using the Bangladesh model",
  "Oikocredit: 45 years of community-based microfinance, survived every financial crisis",
  "Village savings groups (Africa): $2.6B mobilized, serving 14M people, mostly women with no formal credit",
  "Zopa (UK): First P2P platform, 5.1% bad debt rate vs 3.2% for traditional banks - but higher returns for lenders",
  "Chinese ROSCAs: 300 million people use rotating credit associations, embedded in WeChat networks"
];

// STAGE 1: PROBLEM RECOGNITION - Understanding Financial Exclusion
const STAGE_1_CONCEPTS: BigConcept[] = [
  {
    id: 'alternative-financial-services',
    title: 'Alternative Financial Services Fill Real Gaps',
    subtitle: 'Credit unions, payday lenders, and check cashing exist because banks exclude people',
    description: 'When traditional banks say no, alternative services say yes. Understanding why these services exist helps us see the gaps in our financial system.',
    icon: '🏪',
    color: 'from-amber-600 to-orange-600',
    connectors: [
      {
        id: 'bank-exclusion',
        title: 'Why Banks Say No',
        content: '25% of Americans are underbanked or unbanked. Banks exclude people for: thin credit files, low income, geographic barriers, documentation issues, past overdrafts. Meanwhile, community credit unions serve 130 million Americans that big banks often reject.',
        type: 'fact',
        completed: false
      },
      {
        id: 'maria-story',
        title: 'Maria\'s Banking Experience',
        content: 'Maria banks at Wells Fargo but uses check cashing every payday. Why? Her bank charges $35 for overdrafts and holds her paycheck for 2 days. Check cashing costs $6 but gives immediate access. She pays $156/year to avoid $350+ in overdraft fees.',
        type: 'story',
        completed: false
      },
      {
        id: 'credit-union-example',
        title: 'Community Solutions Work',
        content: 'Latino Community Credit Union serves 65,000 members with average incomes of $25,000. They offer $300 loans with 18% APR vs 400% payday loan rates. Member-owned structure means profits return to community through better rates and services.',
        type: 'example',
        completed: false
      }
    ],
    reinforcement: {
      id: 'service-choice-scenario',
      type: 'scenario',
      prompt: 'Jose needs $200 quickly for car repairs. His options: Bank personal loan (3-day approval, needs 650+ credit score), Payday loan (immediate, 400% APR), Credit union loan (next day approval, 18% APR, requires membership). His credit score is 580. What should he do?',
      options: [
        'Take the payday loan - he needs money immediately',
        'Join the credit union and wait for their loan process',
        'Try the bank loan first, then consider alternatives',
        'Look for community assistance programs first'
      ],
      correctAnswer: 'Join the credit union and wait for their loan process',
      explanation: 'Credit unions exist specifically to serve people banks reject. The one-day wait and membership requirement are worth avoiding 400% APR. This teaches the value of building relationships with community-focused institutions.'
    }
  },
  {
    id: 'understanding-fees',
    title: 'Financial Exclusion is Expensive',
    subtitle: 'The "poverty tax" makes being poor more costly',
    description: 'When you\'re excluded from banking, everything costs more. Understanding these fees helps you make better choices and advocate for fair alternatives.',
    icon: '💸',
    color: 'from-red-600 to-pink-600',
    connectors: [
      {
        id: 'poverty-tax-calculation',
        title: 'The Math of Exclusion',
        content: 'Underbanked households pay $2,400+ annually in fees. Check cashing (2-3% per paycheck) = $520/year on $20k income. Payday loans average 8 per year at $55 each. Money orders for bill pay cost $1-5 each. Meanwhile, free checking accounts exist at credit unions.',
        type: 'fact',
        completed: false
      },
      {
        id: 'james-fee-story',
        title: 'James Counts Every Dollar',
        content: 'James works construction and pays $8 every Friday to cash his $400 paycheck. That\'s $416/year. He uses money orders for rent ($3) and utilities ($3 x 3 = $9) monthly = $144/year. Total: $560/year just to access his own money, while his neighbor with a bank account pays $0.',
        type: 'story',
        completed: false
      },
      {
        id: 'fee-comparison',
        title: 'Better Options Exist',
        content: 'Navy Federal Credit Union offers free checking to 13 million members. Local CDFIs provide $300 loans at 18% APR vs 400% payday rates. Even online banks like Chime offer fee-free banking. The expensive services aren\'t the only options.',
        type: 'example',
        completed: false
      }
    ],
    reinforcement: {
      id: 'fee-calculation',
      type: 'scenario',
      prompt: 'Sarah earns $30,000/year and is currently unbanked. She pays: Check cashing 2% of biweekly $1,154 paycheck, 3 money orders/month at $2.89 each, 2 payday loans/year at $55 fee each. How much would she save annually with free banking?',
      options: [
        '$150 per year',
        '$314 per year',
        '$734 per year',
        '$1,200 per year'
      ],
      correctAnswer: '$734 per year',
      explanation: 'Check cashing: $1,154 × 0.02 × 26 = $600. Money orders: $2.89 × 3 × 12 = $104. Payday fees: $55 × 2 = $110. Total: $814 vs $80 in potential account fees = $734 savings. This demonstrates the real cost of financial exclusion.'
    }
  }
];

// FOUNDATION CONCEPTS - Original concepts that build toward TrustPoints
const FOUNDATION_CONCEPTS: BigConcept[] = [
  {
    id: 'trust-replaces-credit',
    title: 'Trust Replaces Credit',
    subtitle: 'Community relationships matter more than credit scores',
    description: 'Traditional credit scores measure your past. Trust scores measure your relationships and community standing. This is the foundation of everything we do.',
    icon: '🤝',
    color: 'from-blue-600 to-teal-600',
    connectors: [
      {
        id: 'credit-problems',
        title: 'Credit Scores Are Broken',
        content: 'Credit scores only look backward. They penalize people for being poor, immigrant, or young. 40% of Americans have "thin" credit files. Meanwhile, in Rwanda, village savings groups serve 2.5M people with 98% repayment rates - no credit bureaus needed.',
        type: 'fact',
        completed: false
      },
      {
        id: 'trust-story',
        title: 'How Communities Really Work',
        content: 'Your neighbor Maria loans you $20 because she knows you. The bank won\'t loan you $20 because your score dropped 3 points when you checked your credit.',
        type: 'story',
        completed: false
      },
      {
        id: 'trust-example',
        title: 'Trust in Action',
        content: 'In Bangladesh, Grameen Bank has 99% repayment rates with no credit checks. They use community trust and group responsibility instead. Meanwhile, US banks with "sophisticated" credit scoring have 3-5% default rates.',
        type: 'example',
        completed: false
      }
    ],
    reinforcement: {
      id: 'trust-scenario',
      type: 'scenario',
      prompt: 'Sarah needs $300 for car repairs. She has a 580 credit score but her community knows she always helps others. How should TrustPoints evaluate her?',
      options: [
        'Reject her - credit score is too low',
        'Approve her - community vouching matters more',
        'Approve but charge higher interest for risk',
        'Ask her to get a co-signer first'
      ],
      correctAnswer: 'Approve her - community vouching matters more',
      explanation: 'Trust scores combine community vouching, past behavior, and relationship strength. A low credit score from past struggles doesn\'t predict future community-backed responsibility.'
    }
  },
  {
    id: 'money-has-relationships',
    title: 'Money Has Relationships',
    subtitle: 'Every financial transaction connects people and builds community',
    description: 'Banks try to hide this, but money is always about relationships. P2P lending makes these relationships visible and valuable.',
    icon: '💝',
    color: 'from-green-600 to-emerald-600',
    connectors: [
      {
        id: 'hidden-connections',
        title: 'Banks Hide Your Connections',
        content: 'When you deposit money, it gets loaned to someone else. But you never know who, and they never know you. Banks profit from hiding these connections.',
        type: 'fact',
        completed: false
      },
      {
        id: 'relationship-story',
        title: 'The Coffee Shop Loan',
        content: 'Tony loans $1,000 to his local coffee shop owner. Every morning, he sees his money helping create jobs and community space. Compare this to his bank savings account - he has no idea where that money goes.',
        type: 'story',
        completed: false
      },
      {
        id: 'network-example',
        title: 'Network Effects',
        content: 'In Kenya, M-Shwari (mobile lending) has 0.9% default rates using social network data instead of credit scores. In China, Ant Financial serves 200M users with AI-powered community scoring, achieving 1% defaults.',
        type: 'example',
        completed: false
      }
    ],
    reinforcement: {
      id: 'relationship-connection',
      type: 'connection',
      prompt: 'Connect the dots: How does making money relationships visible change the entire financial system?',
      explanation: 'Visible relationships create accountability, community wealth building, and align incentives between lenders and borrowers.'
    }
  },
  {
    id: 'risk-is-shared',
    title: 'Risk Is Shared, Not Avoided',
    subtitle: 'True financial security comes from community resilience',
    description: 'Banks try to avoid risk by charging high fees and excluding people. P2P networks share risk across the community, making everyone stronger.',
    icon: '🛡️',
    color: 'from-amber-700 to-orange-700',
    connectors: [
      {
        id: 'bank-risk-hiding',
        title: 'Banks Hide Risk From You',
        content: 'Banks package your deposits with thousands of others, then make risky investments you never see. When they fail, taxpayers bail them out.',
        type: 'fact',
        completed: false
      },
      {
        id: 'community-insurance',
        title: 'Community Insurance Story',
        content: 'When James lost his job, his lending circle covered his loan payments for 3 months. When he recovered, he helped two others do the same.',
        type: 'story',
        completed: false
      },
      {
        id: 'shared-risk-example',
        title: 'Strength Through Sharing',
        content: 'Islamic banking operates on shared-risk principles for 1,400 years and survived 2008 better than traditional banks. Credit unions in the US have 0.6% loan losses vs 0.9% for big banks, despite serving riskier populations.',
        type: 'example',
        completed: false
      }
    ],
    reinforcement: {
      id: 'risk-calculation',
      type: 'scenario',
      prompt: 'A community of 100 people each contribute $100 to an insurance fund. If 2% default each year, how much does each person risk vs. benefit?',
      options: [
        'Each person risks $100, benefits nothing',
        'Each person risks $2, benefits access to $100 loans',
        'Each person risks $50, benefits $50',
        'Risk and benefit are the same as traditional banks'
      ],
      correctAnswer: 'Each person risks $2, benefits access to $100 loans',
      explanation: 'Shared risk means individual risk is minimal (2% of $100 = $2), but individual benefit is maximum (access to $100 in loans when needed).'
    }
  }
];

// Combine all concepts - Stage 1 first, then foundation concepts
const BOOTCAMP_CONCEPTS: BigConcept[] = [
  ...STAGE_1_CONCEPTS,
  ...FOUNDATION_CONCEPTS
];

interface BootcampLearningProps {
  onComplete?: (conceptId: string) => void;
  onProgress?: (conceptId: string, progress: number) => void;
}

export const BootcampLearning: React.FC<BootcampLearningProps> = ({
  onComplete,
  onProgress
}) => {
  const [currentConceptIndex, setCurrentConceptIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<'intro' | 'connectors' | 'reinforcement' | 'complete'>('intro');
  const [connectorIndex, setConnectorIndex] = useState(0);
  const [reinforcementAnswer, setReinforcementAnswer] = useState<string>('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [conceptMastery, setConceptMastery] = useState<{ [key: string]: boolean }>({});

  const currentConcept = BOOTCAMP_CONCEPTS[currentConceptIndex];
  const progress = (currentConceptIndex + (currentPhase === 'complete' ? 1 : 0.5)) / BOOTCAMP_CONCEPTS.length * 100;

  useEffect(() => {
    if (onProgress && currentConcept) {
      onProgress(currentConcept.id, progress);
    }
  }, [currentConceptIndex, currentPhase, onProgress, progress, currentConcept]);

  const nextPhase = () => {
    switch (currentPhase) {
      case 'intro':
        setCurrentPhase('connectors');
        setConnectorIndex(0);
        break;
      case 'connectors':
        if (connectorIndex < currentConcept.connectors.length - 1) {
          setConnectorIndex(connectorIndex + 1);
        } else {
          setCurrentPhase('reinforcement');
        }
        break;
      case 'reinforcement':
        if (reinforcementAnswer === currentConcept.reinforcement.correctAnswer || currentConcept.reinforcement.type === 'connection') {
          setConceptMastery(prev => ({ ...prev, [currentConcept.id]: true }));
          if (onComplete) onComplete(currentConcept.id);

          if (currentConceptIndex < BOOTCAMP_CONCEPTS.length - 1) {
            setCurrentConceptIndex(currentConceptIndex + 1);
            setCurrentPhase('intro');
            setConnectorIndex(0);
            setReinforcementAnswer('');
            setShowExplanation(false);
          } else {
            setCurrentPhase('complete');
          }
        } else {
          setShowExplanation(true);
        }
        break;
    }
  };

  const renderIntro = () => (
    <motion.div
      key="intro"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className="text-center space-y-8 max-w-4xl mx-auto"
    >
      <div className={`text-8xl mb-8`}>
        {currentConcept.icon}
      </div>

      <div className={`bg-gradient-to-r ${currentConcept.color} p-8 rounded-3xl text-white`}>
        <h1 className="text-4xl font-bold mb-4">
          {currentConcept.title}
        </h1>
        <p className="text-xl opacity-90">
          {currentConcept.subtitle}
        </p>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-lg border">
        <p className="text-lg leading-relaxed text-gray-700">
          {currentConcept.description}
        </p>
      </div>

      <button
        onClick={nextPhase}
        className={`bg-gradient-to-r ${currentConcept.color} text-white px-12 py-4 rounded-2xl text-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105`}
      >
        Let's Explore This →
      </button>
    </motion.div>
  );

  const renderConnector = () => {
    const connector = currentConcept.connectors[connectorIndex];

    return (
      <motion.div
        key={`connector-${connectorIndex}`}
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -100 }}
        className="max-w-4xl mx-auto space-y-8"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${currentConcept.color} flex items-center justify-center text-white font-bold text-lg`}>
              {connectorIndex + 1}
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wide text-gray-500">
                {connector.type === 'fact' ? '📊 Key Fact' :
                 connector.type === 'story' ? '📖 Real Story' : '🔍 Example'}
              </h3>
              <h2 className="text-2xl font-bold text-gray-900">{connector.title}</h2>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {connectorIndex + 1} of {currentConcept.connectors.length} pieces
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border">
          <p className="text-lg leading-relaxed text-gray-700">
            {connector.content}
          </p>
        </div>

        <div className="flex justify-between">
          <button
            onClick={() => connectorIndex > 0 ? setConnectorIndex(connectorIndex - 1) : setCurrentPhase('intro')}
            className="px-8 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:border-gray-400 transition-colors"
          >
            ← Back
          </button>

          <button
            onClick={nextPhase}
            className={`bg-gradient-to-r ${currentConcept.color} text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all`}
          >
            {connectorIndex < currentConcept.connectors.length - 1 ? 'Next Piece →' : 'Time to Practice →'}
          </button>
        </div>
      </motion.div>
    );
  };

  const renderReinforcement = () => (
    <motion.div
      key="reinforcement"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className="max-w-4xl mx-auto space-y-8"
    >
      <div className="text-center">
        <div className="text-6xl mb-4">🧠</div>
        <h2 className="text-3xl font-bold mb-2">Let's Make Sure You've Got This</h2>
        <p className="text-gray-600">Practice applying what you just learned</p>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-lg border">
        <p className="text-lg mb-6 leading-relaxed">{currentConcept.reinforcement.prompt}</p>

        {currentConcept.reinforcement.options ? (
          <div className="space-y-3">
            {currentConcept.reinforcement.options.map((option, index) => (
              <button
                key={index}
                onClick={() => setReinforcementAnswer(option)}
                className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                  reinforcementAnswer === option
                    ? `border-blue-500 bg-blue-50`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        ) : (
          <textarea
            value={reinforcementAnswer}
            onChange={(e) => setReinforcementAnswer(e.target.value)}
            placeholder="Write your thoughts here..."
            className="w-full h-32 p-4 border-2 border-gray-200 rounded-xl resize-none focus:border-blue-500 focus:outline-none"
          />
        )}

        {showExplanation && currentConcept.reinforcement.explanation && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl"
          >
            <h4 className="font-semibold text-green-800 mb-2">Here's why:</h4>
            <p className="text-green-700">{currentConcept.reinforcement.explanation}</p>
          </motion.div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => setCurrentPhase('connectors')}
          className="px-8 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:border-gray-400 transition-colors"
        >
          ← Review
        </button>

        <button
          onClick={nextPhase}
          disabled={!reinforcementAnswer}
          className={`px-8 py-3 rounded-xl font-semibold transition-all ${
            reinforcementAnswer
              ? `bg-gradient-to-r ${currentConcept.color} text-white hover:shadow-lg`
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {currentConceptIndex < BOOTCAMP_CONCEPTS.length - 1 ? 'Master This Concept →' : 'Complete Bootcamp →'}
        </button>
      </div>
    </motion.div>
  );

  const renderComplete = () => (
    <motion.div
      key="complete"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center space-y-8 max-w-4xl mx-auto"
    >
      <div className="text-8xl mb-8">🎓</div>
      <h1 className="text-4xl font-bold mb-4">Bootcamp Foundation Complete!</h1>
      <p className="text-xl text-gray-600 mb-8">
        You've mastered the core concepts. Ready for the next phase of your TrustPoints journey.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {BOOTCAMP_CONCEPTS.map((concept) => (
          <div key={concept.id} className="bg-white rounded-xl p-6 shadow-sm border">
            <div className="text-3xl mb-2">{concept.icon}</div>
            <h3 className="font-semibold text-gray-900">{concept.title}</h3>
            <div className="text-green-500 mt-2">✅ Mastered</div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-amber-50 p-8">
      {/* Progress Bar */}
      <div className="max-w-4xl mx-auto mb-12">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-amber-800">Bootcamp Progress</span>
          <span className="text-sm font-medium text-amber-800">{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`bg-gradient-to-r ${currentConcept?.color || 'from-blue-600 to-teal-600'} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Main Content */}
      <AnimatePresence mode="wait">
        {currentPhase === 'intro' && renderIntro()}
        {currentPhase === 'connectors' && renderConnector()}
        {currentPhase === 'reinforcement' && renderReinforcement()}
        {currentPhase === 'complete' && renderComplete()}
      </AnimatePresence>
    </div>
  );
};